import { MediaQuality, MediaType, } from '@signage-player/data-store';
export class EventListService {
    static getMediaFromPlayable(playable, desiredVideoQuality) {
        let quality = desiredVideoQuality;
        if (playable.type === MediaType.IMAGE || playable.type === MediaType.AUDIO) {
            quality = MediaQuality.ORIGINAL;
        }
        else if (playable.type === MediaType.WEB) {
            throw new Error(`Playable type WEB does not have local media file`);
        }
        // Define quality hierarchy from highest to lowest
        const qualityHierarchy = [MediaQuality.ORIGINAL, MediaQuality.P2160, MediaQuality.P1080];
        // Find the desired quality index
        const desiredIndex = qualityHierarchy.indexOf(quality);
        // Try to find media starting from desired quality and going down
        for (let i = desiredIndex; i < qualityHierarchy.length; i++) {
            const media = playable.medias.find(m => m.quality === qualityHierarchy[i]);
            if (media) {
                return media;
            }
        }
        return null;
    }
    static getNextItems(eventListItems, from, count, displayId) {
        // Find the event list for this display
        const eventList = eventListItems.event_lists.find(el => el.display_ids.includes(displayId));
        if (!eventList) {
            return [];
        }
        const firstItemIndex = eventList.items.findIndex(item => new Date(item.to) >= from);
        if (firstItemIndex === -1) {
            return [];
        }
        const upcomingItems = eventList.items.slice(firstItemIndex, firstItemIndex + count);
        return upcomingItems
            .map(item => {
            const playable = eventListItems.playables.find(p => p.id === item.playable_id);
            if (!playable) {
                // Skip items with missing playables instead of crashing
                return null;
            }
            return {
                ...item,
                from: new Date(item.from),
                to: new Date(item.to),
                playable,
            };
        })
            .filter((item) => item !== null);
    }
    static generateEmergencyEventList(cachedEventListItems, previouslyGeneratedEmergencyEventList = null, logger = null) {
        if (cachedEventListItems === null) {
            logger?.debug('[EventListService] No cached event list data available');
            return null;
        }
        const now = new Date();
        const generateTo = new Date();
        generateTo.setHours(generateTo.getHours() + 2);
        const displayResults = [];
        for (const eventList of cachedEventListItems.event_lists) {
            const previousEventList = previouslyGeneratedEmergencyEventList?.event_lists.find(el => el.id === eventList.id);
            const result = this.generateEmergencyEventListForSingleDisplay(eventList, cachedEventListItems.playables, previousEventList ?? null, now, generateTo, logger);
            if (result) {
                displayResults.push(result);
            }
        }
        if (displayResults.length === 0) {
            logger?.debug('[EventListService] No event lists could be generated for any display');
            return null;
        }
        const overallAction = displayResults.some(r => r.action === 'generated') ? 'generated' : 'skipped';
        const totalGenerated = displayResults.reduce((sum, r) => sum + r.generatedCount, 0);
        const latestCoveredUntil = displayResults.reduce((latest, r) => (r.coveredUntil > latest ? r.coveredUntil : latest), displayResults[0].coveredUntil);
        const displays = {};
        for (const r of displayResults) {
            for (const displayId of r.eventList.display_ids) {
                displays[displayId] = {
                    pattern: r.pattern,
                    generatedFrom: r.generatedFrom?.toISOString() ?? null,
                };
            }
        }
        const debug = {
            validServerItemsCount: displayResults.reduce((sum, r) => sum + r.eventList.items.length - r.generatedCount, 0),
            generatedItemsCount: totalGenerated,
            displays,
            coveredUntil: latestCoveredUntil.toISOString(),
        };
        logger?.debug('[EventListService] Emergency event list result', {
            action: overallAction,
            ...debug,
        });
        return {
            action: overallAction,
            eventList: {
                event_lists: displayResults.map(r => r.eventList),
                playables: cachedEventListItems.playables,
            },
            debug,
        };
    }
    static generateEmergencyEventListForSingleDisplay(cachedEventList, playables, previouslyGeneratedEventList, now, generateTo, logger = null) {
        const validCachedItems = cachedEventList.items.filter((item) => new Date(item.to) > now);
        logger?.debug('[EventListService] Processing display', {
            eventListId: cachedEventList.id,
            displayIds: cachedEventList.display_ids,
            totalCachedItems: cachedEventList.items.length,
            validCachedItems: validCachedItems.length,
            hasPreviousEventList: previouslyGeneratedEventList !== null,
        });
        const startingPoint = this.determineStartingPoint(cachedEventList, previouslyGeneratedEventList, validCachedItems, now, generateTo);
        if (startingPoint === null) {
            logger?.debug('[EventListService] No starting point (empty cached items)', { eventListId: cachedEventList.id });
            return null;
        }
        if (startingPoint.earlyReturn !== null) {
            const items = startingPoint.earlyReturn.items;
            const coveredUntil = items.length > 0 ? new Date(items[items.length - 1].to) : now;
            logger?.debug('[EventListService] Early return — previous EL still covers window', {
                eventListId: cachedEventList.id,
                itemCount: items.length,
                coveredUntil: coveredUntil.toISOString(),
            });
            return {
                eventList: startingPoint.earlyReturn,
                action: 'skipped',
                generatedCount: 0,
                pattern: null,
                generatedFrom: null,
                coveredUntil,
            };
        }
        if (startingPoint.startGeneratingFrom >= generateTo) {
            const coveredUntil = startingPoint.startGeneratingFrom;
            logger?.debug('[EventListService] Base items cover window, no generation needed', {
                eventListId: cachedEventList.id,
                baseItemCount: startingPoint.baseItems.length,
                coveredUntil: coveredUntil.toISOString(),
            });
            return {
                eventList: { ...cachedEventList, items: startingPoint.baseItems },
                action: 'skipped',
                generatedCount: 0,
                pattern: null,
                generatedFrom: null,
                coveredUntil,
            };
        }
        const itemsWithPlayables = this.prepareItemsWithPlayables(cachedEventList.items, playables);
        const pattern = this.findPattern(itemsWithPlayables);
        if (pattern === null) {
            if (validCachedItems.length === 0) {
                logger?.debug('[EventListService] No pattern found and no valid items', { eventListId: cachedEventList.id });
                return null;
            }
            const coveredUntil = new Date(validCachedItems[validCachedItems.length - 1].to);
            logger?.debug('[EventListService] No pattern found, returning valid cached items only', {
                eventListId: cachedEventList.id,
                validItemCount: validCachedItems.length,
                coveredUntil: coveredUntil.toISOString(),
            });
            return {
                eventList: { ...cachedEventList, items: validCachedItems },
                action: 'skipped',
                generatedCount: 0,
                pattern: null,
                generatedFrom: null,
                coveredUntil,
            };
        }
        const patternPlayableIds = pattern.map(p => p.playable.id);
        const sortedElements = this.preparePatternWithLengths(pattern, itemsWithPlayables);
        let from = startingPoint.startGeneratingFrom;
        let index = this.determineInitialPatternIndex(previouslyGeneratedEventList, startingPoint.baseItems, sortedElements);
        if (from < now) {
            const skipResult = this.skipForwardThroughPattern(from, now, sortedElements, index);
            from = skipResult.from;
            index = skipResult.index;
        }
        const generatedFrom = new Date(from);
        const emergencyItems = this.generateItemsFromPattern(startingPoint.baseItems, from, generateTo, startingPoint.nextId, sortedElements, index);
        const finalItems = emergencyItems.filter(item => new Date(item.to) > now);
        const generatedCount = finalItems.length - startingPoint.baseItems.filter(item => new Date(item.to) > now).length;
        const coveredUntil = finalItems.length > 0 ? new Date(finalItems[finalItems.length - 1].to) : now;
        logger?.debug('[EventListService] Generated emergency items using pattern', {
            eventListId: cachedEventList.id,
            pattern: patternPlayableIds,
            patternLengths: sortedElements.map(el => el.length_ms),
            generatedFrom: generatedFrom.toISOString(),
            generatedCount,
            totalItems: finalItems.length,
            coveredUntil: coveredUntil.toISOString(),
        });
        return {
            eventList: { ...cachedEventList, items: finalItems },
            action: 'generated',
            generatedCount,
            pattern: patternPlayableIds,
            generatedFrom,
            coveredUntil,
        };
    }
    static determineStartingPoint(cachedEventList, previouslyGeneratedEventList, validCachedItems, now, generateTo) {
        if (cachedEventList.items.length === 0) {
            return null;
        }
        let startGeneratingFrom;
        let baseItems;
        let nextId;
        if (previouslyGeneratedEventList !== null) {
            const validPreviousItems = previouslyGeneratedEventList.items.filter((item) => new Date(item.to) > now);
            if (validPreviousItems.length > 0) {
                const lastPreviousItem = validPreviousItems[validPreviousItems.length - 1];
                const lastPreviousItemDate = new Date(lastPreviousItem.to);
                if (lastPreviousItemDate >= generateTo) {
                    return {
                        baseItems: validPreviousItems,
                        startGeneratingFrom: lastPreviousItemDate,
                        nextId: 0,
                        earlyReturn: {
                            ...previouslyGeneratedEventList,
                            items: validPreviousItems,
                        },
                    };
                }
                baseItems = validPreviousItems;
                startGeneratingFrom = lastPreviousItemDate;
                nextId = Math.max(...previouslyGeneratedEventList.items.map((i) => i.id)) + 1;
            }
            else {
                baseItems = validCachedItems;
                startGeneratingFrom =
                    validCachedItems.length > 0
                        ? new Date(validCachedItems[validCachedItems.length - 1].to)
                        : new Date(cachedEventList.items[cachedEventList.items.length - 1].to);
                nextId = Math.max(...cachedEventList.items.map((i) => i.id), 0) + 1;
            }
        }
        else {
            baseItems = validCachedItems;
            startGeneratingFrom =
                validCachedItems.length > 0
                    ? new Date(validCachedItems[validCachedItems.length - 1].to)
                    : new Date(cachedEventList.items[cachedEventList.items.length - 1].to);
            nextId = Math.max(...cachedEventList.items.map((i) => i.id), 0) + 1;
        }
        return {
            baseItems,
            startGeneratingFrom,
            nextId,
            earlyReturn: null,
        };
    }
    static prepareItemsWithPlayables(items, playables) {
        return items.map(item => {
            const playable = playables.find(p => p.id === item.playable_id);
            if (!playable) {
                throw new Error(`Playable with ID ${item.playable_id} not found`);
            }
            return {
                ...item,
                playable,
            };
        });
    }
    static preparePatternWithLengths(pattern, itemsWithPlayables) {
        const patternWithLength = pattern.map(item => ({
            ...item,
            length_ms: new Date(item.to).getTime() - new Date(item.from).getTime(),
        }));
        const lastElements = itemsWithPlayables.slice(-pattern.length);
        return lastElements.map(el => {
            const found = patternWithLength.find(item => item.playable.id === el.playable.id);
            if (!found) {
                throw new Error(`Pattern item with playable ID ${el.playable.id} not found`);
            }
            return found;
        });
    }
    static determineInitialPatternIndex(previouslyGeneratedEventList, baseItems, sortedElements) {
        if (previouslyGeneratedEventList !== null && baseItems.length > 0) {
            const lastItem = baseItems[baseItems.length - 1];
            const patternIndex = sortedElements.findIndex(item => item.playable.id === lastItem.playable_id);
            if (patternIndex !== -1) {
                return (patternIndex + 1) % sortedElements.length;
            }
        }
        return 0;
    }
    static skipForwardThroughPattern(from, now, sortedElements, initialIndex) {
        const patternDuration = sortedElements.reduce((sum, el) => sum + el.length_ms, 0);
        const timeToSkip = now.getTime() - from.getTime();
        const completeCycles = Math.floor(timeToSkip / patternDuration);
        const remainingTime = timeToSkip % patternDuration;
        let newFrom = new Date(from.getTime() + completeCycles * patternDuration);
        let index = initialIndex;
        let accumulatedTime = 0;
        while (accumulatedTime + sortedElements[index].length_ms <= remainingTime) {
            accumulatedTime += sortedElements[index].length_ms;
            newFrom = new Date(newFrom.getTime() + sortedElements[index].length_ms);
            index++;
            if (index >= sortedElements.length) {
                index = 0;
            }
        }
        return { from: newFrom, index };
    }
    static generateItemsFromPattern(baseItems, from, generateTo, nextId, sortedElements, initialIndex) {
        const emergencyItems = [...baseItems];
        let currentFrom = from;
        let id = nextId;
        let index = initialIndex;
        while (currentFrom < generateTo) {
            const patternItem = sortedElements[index];
            const newItem = {
                id: id++,
                from: currentFrom.toISOString(),
                to: new Date(currentFrom.getTime() + patternItem.length_ms).toISOString(),
                playable_id: patternItem.playable.id,
                start_at: patternItem.start_at,
                is_spot: patternItem.is_spot,
            };
            emergencyItems.push(newItem);
            currentFrom = new Date(newItem.to);
            index++;
            if (index >= sortedElements.length) {
                index = 0;
            }
        }
        return emergencyItems;
    }
    static findPattern(items) {
        const n = items.length;
        // Try pattern lengths from 1 to half the array length
        for (let patternLen = 1; patternLen <= Math.floor(n / 2); patternLen++) {
            const pattern = items.slice(0, patternLen);
            let valid = true;
            // Check if this pattern repeats throughout the array
            for (let i = 0; i < n; i++) {
                if (items[i].playable.id !== pattern[i % patternLen].playable.id) {
                    valid = false;
                    break;
                }
            }
            if (valid) {
                return pattern;
            }
        }
        return null;
    }
}
//# sourceMappingURL=EventListService.js.map